import React, { useCallback, useMemo, useState } from 'react';
import { App } from 'antd';

import ShareModal from '../common/ShareModal';
import { loadChartExport } from '../charts/LazyCharts';
import { useOptionalTheme } from '../../contexts/ThemeContext';
import useDateFormat from '../../hooks/useDateFormat';
import useTimeFormat from '../../hooks/useTimeFormat';
import { failedSlot, runCharts, runChartForExport } from '../../services/charts';
import { createShortLink } from '../../services/shortLinks';
import { copyTextToClipboard } from '../../utils/clipboard';
import { TIME_FORMAT_PATTERNS } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { buildShortLinkUrl } from '../../utils/shareUrls';
import { slugify } from '../../utils/strings';
import { chartPath } from './ChartRow';
import { toShortPath } from '../../utils/recordCodes';
import { anchorOf, siteOrigin } from '../../platform';
import { cardElementChain, chartSpecForDisplay, columnForField, columnRefOf, elementForField, fieldIdOf } from '../../utils/chartSpecFields';

// A short link's path holds at most this many characters (keepr-api
// utils/shortLinkUtils.js MAX_PATH); a longer address is copied whole.
const SHORT_LINK_PATH_MAX = 500;
const NO_CHUNK = "The chart's drawing code didn't load. Refresh the page and try again.";

/**
 * The element a chart groups or splits by, from its card's elements, for a
 * choice's own colours (q15): as the spec names it — by name, or by id
 * (`#k7f3q2xa`, KPR-238 P6; utils/chartSpecFields.js), an id a restatement up
 * the card's chain replaced read through `chain` (cardElementChain, W1).
 */
export function elementNamed(list, name, chain = []) {
	if (typeof name !== 'string' || !name) return null;
	// A repeating group's column (KPR-684): `<group>.<column>`, its own definition.
	if (columnRefOf(name)) {
		const col = columnForField(list, name, { shadowed: chain });
		return col ? col.column : null;
	}
	if (fieldIdOf(name) !== null) return elementForField(list, name, { shadowed: chain });
	return list.find((e) => e && e.name === name)
		|| list.find((e) => e && typeof e.name === 'string' && e.name.toLowerCase() === name.toLowerCase())
		|| null;
}

/**
 * `{ group, split, list, chain }` of a chart's card (CardDefinitionsContext's
 * getElementsForCard, `cardsById` and `elementSetsById`): the group and split
 * element definitions, every element of the card, and every element its
 * chain carries unmerged (cardElementChain) — what ChartFrame and a download
 * say a field holding an id by (chartSpecForDisplay).
 */
export function chartElements(chart, getElementsForCard, { cardsById = null, elementSetsById = null } = {}) {
	const cardId = normalizeId(chart?.spec?.card_id);
	if (!cardId || typeof getElementsForCard !== 'function' || !chart?.spec) return {};
	const list = (getElementsForCard(cardId) || []).filter(Boolean);
	const chain = cardsById ? cardElementChain(cardId, cardsById, elementSetsById) : [];
	return { group: elementNamed(list, chart.spec.groupBy?.on, chain), split: elementNamed(list, chart.spec.splitBy?.on, chain), list, chain };
}

/** The chart as a file's words read it: a spec field holding an id as its element's name (chartSpecForDisplay). */
function shownChart(chart, elements) {
	return chart && chart.spec ? { ...chart, spec: chartSpecForDisplay(chart.spec, elements && elements.list, elements && elements.chain) } : chart;
}

/** What a download that left items out says (the answer's `export_omitted`). */
export function omittedSentence(count) {
	return count === 1
		? '1 item you can see may not be exported, so the file leaves it out.'
		: `${count} items you can see may not be exported, so the file leaves them out.`;
}

/**
 * SHARE & EXPORT for a saved chart (plan §8, A9): the chart page's ⋯ and the
 * Reports row's ⋯ call these, so a chart offers the same three wherever it is.
 *
 *   downloadData(chart, { range, cardName, elements })
 *       Download data. An export (⚑ d25): the chart runs again at the EXPORT
 *       grade (services/charts runChartForExport) — what an item export would
 *       hand this person, never the numbers on screen — with the range the
 *       page shows, and that answer becomes `<chart name>.csv`, built from the
 *       table twin's rows in the charts chunk (chartExport.js), the chart's
 *       notes under them (`cardName` words them as the frame does). A file
 *       that left out items the person can see says how many here too.
 *       `elements` (chartElements) names a spec field that holds an id.
 *   downloadImage(chart, { result, range, elements })
 *       Download image. The answer on screen (`result`; a Reports row has
 *       none, so it runs the chart first) drawn as `<chart name>.png` at twice
 *       its pixels on the chart's surface, in the reader's theme and mode.
 *   copyLink(chart, { search })
 *       Copy link. A short link (`kind: 'path'`, the short-link service every
 *       share surface uses) to the chart's page — with `search`, the query
 *       the page builds from what it shows (its date range, an open table;
 *       never the address as typed) — copied. An address longer
 *       than a short link holds, or a short link that could not be made, is
 *       copied whole. Where the browser will not copy without a fresh click
 *       (Safari, after the request), the share window opens with the link
 *       and its Copy button (ShareModal, the platform's copy action).
 *
 * Returns { downloadData, downloadImage, copyLink, busy, modal }: `busy` is
 * 'data', 'image' or 'link' while one runs; render `modal`.
 */
export default function useChartShare({ collectionId }) {
	const { message } = App.useApp();
	const themeCtx = useOptionalTheme();
	const dateFormat = useDateFormat();
	const timeFormat = useTimeFormat();
	const prefs = useMemo(() => ({
		dateFormat,
		timePattern: Object.prototype.hasOwnProperty.call(TIME_FORMAT_PATTERNS, timeFormat) ? TIME_FORMAT_PATTERNS[timeFormat] : TIME_FORMAT_PATTERNS['12h'],
	}), [dateFormat, timeFormat]);
	const [busy, setBusy] = useState(null);
	const [shared, setShared] = useState(null); // { url, name } while the share window is open

	const exporter = useCallback(async () => {
		try {
			return await loadChartExport();
		} catch (e) {
			message.error(NO_CHUNK);
			return null;
		}
	}, [message]);

	const downloadData = useCallback(async (chart, { range, cardName, elements } = {}) => {
		const anchor = anchorOf();
		const chartId = normalizeId(chart?._id);
		if (!chartId || !collectionId) return;
		setBusy('data');
		try {
			const [slot, mod] = await Promise.all([runChartForExport({ chartId, collectionId, range }), exporter()]);
			if (!mod) return;
			const out = await mod.saveChartData({ result: slot, chart: shownChart(chart, elements), prefs, cardName, anchor });
			if (!out.ok) message.error(out.message);
			else if (out.omitted > 0) message.warning(omittedSentence(out.omitted));
		} finally {
			setBusy(null);
		}
	}, [collectionId, exporter, message, prefs]);

	const downloadImage = useCallback(async (chart, { result, range, elements } = {}) => {
		const anchor = anchorOf();
		const chartId = normalizeId(chart?._id);
		if (!chartId || !collectionId) return;
		setBusy('image');
		try {
			let answer = result && result.ok === true ? result : null;
			const [mod] = await Promise.all([exporter(), (async () => {
				if (answer) return;
				const request = { chart_id: chartId, collection_id: collectionId };
				if (range !== undefined) request.range = range;
				try {
					answer = (await runCharts([request])).results[0] || failedSlot(null, chartId);
				} catch (e) {
					answer = failedSlot(e, chartId);
				}
			})()]);
			if (!mod) return;
			const out = await mod.saveChartImage({
				result: answer, chart: shownChart(chart, elements), themeKey: themeCtx?.theme, mode: themeCtx?.mode, prefs, elements, anchor,
			});
			if (!out.ok) message.error(out.message);
		} finally {
			setBusy(null);
		}
	}, [collectionId, exporter, message, prefs, themeCtx]);

	const copyLink = useCallback(async (chart, { search = '' } = {}) => {
		const chartId = normalizeId(chart?._id);
		if (!chartId || !collectionId) return;
		// The collection's short form when its code is known (/c/<code>/charts/…), in the
		// /s/ link it stores and in the address it falls back to.
		const path = toShortPath(`${chartPath(collectionId, chartId)}${search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : ''}`);
		const origin = siteOrigin();
		const whole = origin ? `${origin}${path}` : path;
		setBusy('link');
		let url = whole;
		try {
			if (path.length <= SHORT_LINK_PATH_MAX) {
				try {
					const data = await createShortLink({ kind: 'path', path });
					url = data?.url || buildShortLinkUrl(data?.code) || whole;
				} catch (e) {
					url = whole; // the address itself still opens the chart
				}
			}
			if (await copyTextToClipboard(url)) message.success('Link copied to clipboard');
			else setShared({ url, name: chart?.name || 'Chart' });
		} finally {
			setBusy(null);
		}
	}, [collectionId, message]);

	const modal = (
		<ShareModal
			open={Boolean(shared)}
			onClose={() => setShared(null)}
			modalTitle='Link to this chart'
			eyebrow='Chart'
			previewTitle={shared?.name || 'Chart'}
			shareUrl={shared?.url || ''}
			downloadFileName={`keepr-chart-${slugify(shared?.name || '') || 'chart'}`}
			className='chart-share-modal'
		/>
	);

	return { downloadData, downloadImage, copyLink, busy, modal };
}
